import React, { useState, useCallback } from "react";
import { Collapse, Button, FormControl, Form } from "react-bootstrap";
import { FaPlus, FaMinus } from "react-icons/fa";
import "../../styles/components/FilterModal.css";

// Helper function to format the button text
const getButtonText = (node) => {
  let name = node.name
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return `${name} (${node.id})`;
};

const HierarchicalCollapse = ({
  node,
  level = 0,
  selectedDiseases,
  handleCheckboxChange,
  disable,
}) => {
  const [open, setOpen] = useState(level === 0);
  const [childrenLoaded, setChildrenLoaded] = useState(level === 0);
  const hasChildren = node.children && node.children.length > 0;
  const paddingN = 0.8;

  const isSelected = selectedDiseases.some((disease) => disease.id === node.id);

  const handleToggle = () => {
    if (!disable) {
      setOpen(!open);
      if (!childrenLoaded) {
        setChildrenLoaded(true);
      }
      if (!hasChildren) {
        handleCheckboxChange(node.id, node.name);
      }
    }
  };

  const nodeStyle = {
    paddingLeft: `${level * paddingN}rem`,
  };

  return (
    <div className="hierarchy-container" style={nodeStyle}>
      {level > 0 && (
        <>
          <div
            className="hierarchy-line"
            style={{ left: `${(level - 1) * paddingN}rem` }}
          />
          <div
            className="hierarchy-line-horizontal"
            style={{ top: "1.5rem", left: `${(level - 1) * paddingN}rem` }}
          />
        </>
      )}

      <div className="d-flex justify-content-between align-items-center hierarchy-row">
        <Button
          onClick={handleToggle}
          aria-controls={`collapse-${node.id}`}
          aria-expanded={open}
          className="fit-text-button"
          style={{
            border: "none",
            color: disable ? "#6c757d" : "black",
            backgroundColor: "transparent",
          }}
          disabled={disable}
        >
          {getButtonText(node)}
          {hasChildren && (
            <span className={`icon-box ${open ? "open" : ""}`}>
              {open ? <FaMinus size={12} /> : <FaPlus size={12} />}
            </span>
          )}
        </Button>

        <Form.Check
          type="checkbox"
          checked={isSelected}
          onChange={() => !disable && handleCheckboxChange(node.id, node.name)}
          disabled={disable}
        />
      </div>

      {hasChildren && (
        <Collapse in={open}>
          <div id={`collapse-${node.id}`} className="pl-3">
            {childrenLoaded &&
              node.children.map((child) => (
                <HierarchicalCollapse
                  key={child.id}
                  node={child}
                  level={level + 1}
                  selectedDiseases={selectedDiseases}
                  handleCheckboxChange={handleCheckboxChange}
                  disable={disable}
                />
              ))}
          </div>
        </Collapse>
      )}
    </div>
  );
};

const HierarchicalMenu = ({
  data,
  selectedDiseases,
  setSelectedDiseases,
  confidence_score,
  setConfidenceScore,
  DiseaseDatasets,
  setDiseaseDatasets,
  disable,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const handleCheckboxChange = useCallback(
    (id, name) => {
      if (!disable) {
        setSelectedDiseases((prevSelected) => {
          const isSelected = prevSelected.some((disease) => disease.id === id);
          if (isSelected) {
            return prevSelected.filter((disease) => disease.id !== id);
          } else {
            return [...prevSelected, { id, name }];
          }
        });
      }
    },
    [disable, setSelectedDiseases]
  );

  const filterNodes = (node, query) => {
    const isMatch = node.name.toLowerCase().includes(query.toLowerCase());

    if (!node.children || node.children.length === 0) {
      return isMatch ? node : null;
    }

    const filteredChildren = node.children
      .map((child) => filterNodes(child, query))
      .filter((child) => child !== null);

    if (isMatch || filteredChildren.length > 0) {
      return { ...node, children: filteredChildren };
    }

    return null;
  };

  const filteredData = filterNodes(data, searchQuery);

  return (
    <div className="filter-modal-body">
      <div className="mb-3">
        <label htmlFor="search" className="form-label">
          Disease Filter
        </label>
        <FormControl
          type="text"
          placeholder="Filter diseases by name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={disable}
        />
      </div>

      <div className="mb-3">
        <label htmlFor="confidence_score" className="form-label">
          Confidence Score: {confidence_score}
        </label>
        <input
          type="range"
          id="confidence_score"
          name="confidence_score"
          min="0"
          max="5"
          step="0.1"
          value={confidence_score}
          onChange={(e) => setConfidenceScore(e.target.value)}
          className="form-range"
          disabled={disable}
        />
      </div>

      <div className="mb-3">
        <label className="form-label d-block">Disease Datasets</label>

        <div className="form-check form-check-inline">
          <input
            className="form-check-input"
            type="checkbox"
            value="knowledge"
            checked={DiseaseDatasets && DiseaseDatasets.includes("knowledge")}
            onChange={(e) => {
              const value = e.target.value;
              if (e.target.checked) {
                setDiseaseDatasets([...(DiseaseDatasets || []), value]);
              } else {
                setDiseaseDatasets(
                  (DiseaseDatasets || []).filter((dataset) => dataset !== value)
                );
              }
            }}
            disabled={disable}
          />
          <label className="form-check-label">Knowledge</label>
        </div>

        <div className="form-check form-check-inline">
          <input
            className="form-check-input"
            type="checkbox"
            value="experiments"
            checked={DiseaseDatasets && DiseaseDatasets.includes("experiments")}
            onChange={(e) => {
              const value = e.target.value;
              if (e.target.checked) {
                setDiseaseDatasets([...(DiseaseDatasets || []), value]);
              } else {
                setDiseaseDatasets(
                  (DiseaseDatasets || []).filter((dataset) => dataset !== value)
                );
              }
            }}
            disabled={disable}
          />
          <label className="form-check-label">Experiments</label>
        </div>

      </div>

      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div style={{ maxWidth: "70%" }}>
          <p className="mb-1">
            <strong>Selected Diseases:</strong>{" "}
            <span className="badge bg-primary ms-1">
              {selectedDiseases.length}
            </span>
          </p>
          {selectedDiseases.length > 0 && (
            <small className="text-muted text-truncate d-block">
              {selectedDiseases
                .slice(0, 3)
                .map((disease) => disease.name)
                .join(", ")}
              {selectedDiseases.length > 3 &&
                ` ... and ${selectedDiseases.length - 3} more`}
            </small>
          )}
        </div>
        <Button
          onClick={() => setSelectedDiseases([])}
          variant="danger"
          className="mb-2 btn-animate"
          disabled={disable || selectedDiseases.length === 0}
        >
          Clear
        </Button>
      </div>

      <hr />

      <div className="hierarchicalcollapse">
        {filteredData ? (
          <HierarchicalCollapse
            node={filteredData}
            selectedDiseases={selectedDiseases}
            handleCheckboxChange={handleCheckboxChange}
            disable={disable}
          />
        ) : (
          <p>No matches found</p>
        )}
      </div>
    </div>
  );
};

export default HierarchicalMenu;
